import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { bindActionCreators } from '@reduxjs/toolkit';
import { WebAuth } from 'auth0-js';
import classnames from 'classnames';
import React, {
	ChangeEvent,
	KeyboardEvent,
	useEffect,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
	createSearchParams,
	useLocation,
	useNavigate
} from 'react-router-dom';
import { Button, Loader } from 'vio-components';
import {
	getAuth0ClientId,
	getAuth0Domain,
	usePrevious,
	useReduxState,
	validateEmail
} from 'vio-libs';

import {
	IAuthState,
	ILoginState,
	IVaporIO
} from '../interfaces';
import {
	failedAuth0Authentication,
	requestSetServerSideCookie,
	resetAuth0,
	resetUnauthorized
} from '../redux';
import { useAppDispatch } from '../redux/hooks';

declare const vaporio: IVaporIO;

const initialLoginState: ILoginState = {
	accessToken: undefined,
	databaseLogin: false,
	email: '',
	isEmailValid: true,
	password: '',
	requestingAuthentication: false,
	showPassword: false
};

const Login: React.FC = (): JSX.Element => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();

	const wss = window.sessionStorage;

	const webAuth = new WebAuth({
		clientID: getAuth0ClientId(vaporio),
		domain: getAuth0Domain(vaporio),
		redirectUri: `${window.location.origin}/auth`,
		responseType: 'token'
	});

	const getOAuthAccessToken = (): string => {
		const { hash } = location;
		const hashParams = createSearchParams(hash.substring(1));

		return hashParams.get('access_token');
	};

	const [ loginState, setLoginState ] = useState<ILoginState>({
		...initialLoginState,
		accessToken: getOAuthAccessToken()
	});
	const {
		accessToken,
		databaseLogin,
		email,
		isEmailValid,
		password,
		requestingAuthentication,
		showPassword
	} = loginState;

	const {
		auth0Error,
		requestingSetServerSideCookie,
		unauthorized
	} = useReduxState<IAuthState>('auth');
	const previousRequestingSetServerSideCookie = usePrevious(requestingSetServerSideCookie);

	const failedAuth0AuthenticationDispatch = bindActionCreators(failedAuth0Authentication, dispatch);
	const requestSetServerSideCookieDispatch = bindActionCreators(requestSetServerSideCookie, dispatch);
	const resetAuth0Dispatch = bindActionCreators(resetAuth0, dispatch);
	const resetUnauthorizedDispatch = bindActionCreators(resetUnauthorized, dispatch);

	const continueToLogin = (): void => {
		const isEmailValid = validateEmail(email);

		if (auth0Error) {
			resetAuth0Dispatch();
		}

		if (unauthorized) {
			resetUnauthorizedDispatch();
		}

		if (isEmailValid && email.toLowerCase() === 'e2e-testing@vapor.io' && !databaseLogin) {
			setLoginState({
				...loginState,
				databaseLogin: true,
				isEmailValid: true
			});

			return;
		}
		else if (isEmailValid && !requestingAuthentication && !databaseLogin) {
			wss.setItem('userID', email);

			setLoginState({
				...loginState,
				isEmailValid: true,
				requestingAuthentication: true
			});

			webAuth.authorize({
				connection: 'google-oauth2',
				login_hint: email
			});

			return;
		}

		if (databaseLogin && password) {
			setLoginState({
				...loginState,
				requestingAuthentication: true
			});

			webAuth.login(
				{
					password,
					realm: 'Username-Password-Authentication',
					username: email
				},
				(err, authResult) => {
					setLoginState({
						...loginState,
						requestingAuthentication: false
					});

					if (err) {
						const { description } = err;

						failedAuth0AuthenticationDispatch(description);
					}
					else {
						onAuth0LockAuthentication(authResult);
					}
				}
			);

			return;
		}
		else if (databaseLogin && !password) {
			failedAuth0AuthenticationDispatch(t('PASSWORD_FIELD_REQUIRED'));
		}

		setLoginState({
			...loginState,
			isEmailValid
		});
	};

	const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setLoginState({
			...loginState,
			email: event.target.value
		});
	};

	const handleKeyDown = (event: KeyboardEvent): void => {
		if (event.key === 'Enter') {
			continueToLogin();
		}
	};

	const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setLoginState({
			...loginState,
			password: event.target.value
		});
	};

	const onAuth0LockAuthentication = (auth0Authentication): void => {
		requestSetServerSideCookieDispatch(auth0Authentication);
	};

	const resetDatabaseLogin = (): void => {
		resetAuth0Dispatch();
		setLoginState({
			...loginState,
			databaseLogin: false,
			password: '',
			showPassword: false
		});
	};

	const resetState = (): void => {
		setLoginState(initialLoginState);
	};

	const togglePasswordVisibility = (): void => {
		setLoginState({
			...loginState,
			showPassword: !showPassword
		});
	};

	useEffect(() => {
		const { hash } = location;
		const userID = wss.getItem('userID');

		if (hash) {
			// Parse the URL and extract the Access Token
			webAuth.parseHash((err, authResult) => {
				if (err) {
					const { errorDescription } = err;

					failedAuth0AuthenticationDispatch(errorDescription);
				}
				else if (authResult.idTokenPayload?.email !== userID) {
					failedAuth0AuthenticationDispatch(t('USER_NOT_FOUND_IN_IDENTITY_PROVIDER'));
					navigate('/');
				}
				else {
					onAuth0LockAuthentication(authResult);
				}
			});
		}

		return function cleanUp() {
			resetState();
		};
	}, []);

	useEffect(() => {
		// This block will only run if authentication was already successful
		if (previousRequestingSetServerSideCookie && !requestingSetServerSideCookie) {
			navigate('/');
		}
	}, [ requestingSetServerSideCookie ]);

	const renderAuth0Lock = (): JSX.Element => {
		if (accessToken) {
			return <Loader message={ t('LOGIN_MESSAGE') } />;
		}

		return (
			<div className='auth0-lock'>
				<div className='header'>
					<span className='title'>
						{ t('LOG_IN_TO_YOUR_ACCOUNT') }
					</span>

					<span>
						{ databaseLogin ? t('ENTER_YOUR_PASSWORD') : t('ENTER_YOUR_EMAIL_TO_CONTINUE') }
					</span>
				</div>

				<div className='user-input'>
					{ renderEmailField() }
					{ renderPasswordField() }

					<Button
						variant='info'
						className='continue-btn'
						onClick={ continueToLogin }>
						{ t('CONTINUE') }
					</Button>
				</div>

				{ renderErrors() }
				{ requestingAuthentication && <Loader /> }
			</div>
		);
	};

	const renderEmailField = (): JSX.Element => {
		const emailClassName = classnames('email', {
			error: !isEmailValid
		});

		if (databaseLogin) {
			return (
				<div className={ emailClassName }>
					<input
						disabled
						type='email'
						value={ email } />

					<button className='edit-btn' onClick={ resetDatabaseLogin }>
						{ t('EDIT') }
					</button>
				</div>
			);
		}

		return (
			<input
				autoFocus
				className={ emailClassName }
				onChange={ handleEmailChange }
				onKeyDown={ handleKeyDown }
				placeholder={ t('EMAIL_ADDRESS') }
				spellCheck={ false }
				type='email'
				value={ email } />
		);
	};

	const renderErrors = (): JSX.Element | null => {
		if (auth0Error || unauthorized || !isEmailValid) {
			return (
				<div className='errors'>
					{ auth0Error &&
						<span>
							{ `${t('ERROR')}: ${auth0Error}` }
						</span>
					}

					{ unauthorized &&
						<span>
							{ `${t('ERROR')}: ${t('UNAUTHORIZED_ACCESS')}` }
						</span>
					}

					{ !isEmailValid &&
						<span>
							{ `${t('ERROR')}: ${t('USER_EMAIL_NOT_VALID')}` }
						</span>
					}
				</div>
			);
		}

		return null;
	};

	const renderPasswordField = (): JSX.Element | null => {
		if (databaseLogin) {
			const passwordClassName = classnames('password', {
				error: auth0Error
			});
			const iconTitle = showPassword ? t('HIDE_PASSWORD') : t('SHOW_PASSWORD');
			const inputType = showPassword ? 'text' : 'password';

			return (
				<div className={ passwordClassName }>
					<input
						autoFocus
						onChange={ handlePasswordChange }
						onKeyDown={ handleKeyDown }
						placeholder={ t('PASSWORD') }
						spellCheck={ false }
						type={ inputType }
						value={ password } />

					<button className='toggle-password-btn' onClick={ togglePasswordVisibility } title={ iconTitle }>
						<FontAwesomeIcon icon={ showPassword ? faEyeSlash : faEye } transform='shrink-1' />
					</button>
				</div>
			);
		}

		return null;
	};

	return (
		<div id='login'>
			{ renderAuth0Lock() }
		</div>
	);
};

export default Login;
